import { useMemo, useState } from "react";

import { isImpersonatedSession } from "@/auth/token";
import { isUserApprovedToCall } from "@/helpers/campaign";
import { useGlobalContext } from "@/hooks/use-global-context";
import { useManageMe } from "@/hooks/use-manage-me";

export interface DailyTodosViewerI {
  /**
   * The viewer has To-Dos: a caller or Pod Lead (C16-D471) approved to call
   * on a campaign. LGAs and Admins don't, and nothing asks the server for
   * them. Unknown while manage/me loads or fails (false: fail closed).
   */
  isCaller: boolean;
  /** An Admin support session: the page reads, the gate never opens. */
  isImpersonated: boolean;
  /** manage/me answered (or failed) and the user's load finished. */
  isKnown: boolean;
  /** manage/me failed: who the viewer is can't be told (retry with refetch). */
  hasFailed: boolean;
  /** A retry of a failed manage/me is in flight. */
  isRetrying: boolean;
  refetch: () => void;
}

/**
 * Who sees Leads → To-Dos (C-16 S6). One answer for the nav item, the page,
 * the polling and the gate, so none of them asks the server for a viewer
 * who has no To-Dos.
 */
export const useDailyTodosViewer = (): DailyTodosViewerI => {
  const manage = useManageMe();
  const { campaigns, isUserLoaded } = useGlobalContext();
  // Read once: an impersonation starts and ends with a page load.
  const [isImpersonatedCookie] = useState(isImpersonatedSession);

  return useMemo(() => {
    const isImpersonated = isImpersonatedCookie || manage.impersonating;
    const isApproved = !!campaigns?.some((campaign) =>
      isUserApprovedToCall(campaign.campaign_user_status)
    );

    let hasCallerRole = false;

    if (manage.status === "off") {
      hasCallerRole = true;
    } else if (manage.status === "on") {
      hasCallerRole = !manage.isAdmin && !manage.isLga;
    }

    return {
      isCaller: hasCallerRole && isApproved,
      isImpersonated,
      // isUserLoaded is set once the user's requests settle, failures too,
      // so a failed campaigns load can't leave this unknown for good.
      isKnown:
        manage.status !== "loading" &&
        (campaigns !== undefined || !!isUserLoaded),
      hasFailed: manage.status === "error",
      isRetrying: manage.status === "error" && manage.isFetching,
      refetch: manage.refetch,
    };
  }, [
    campaigns,
    isUserLoaded,
    isImpersonatedCookie,
    manage.impersonating,
    manage.isAdmin,
    manage.isFetching,
    manage.isLga,
    manage.refetch,
    manage.status,
  ]);
};
